import { useEffect, useRef, useState } from "react";

/* Lightweight scroll-reveal — a subtle fade-up "pop" the first time an element
   enters the viewport. Uses IntersectionObserver + a CSS transition, so it needs
   no external animation library. Shared by the landing sections and the About page.

   Props:
   - as:        element/tag to render (default "div")
   - delay:     transition delay in ms (use to stagger siblings)
   - className: extra classes applied to the element */
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const Reveal = ({ as: Tag = "div", delay = 0, className = "", children }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Never hide content from users who can't get the animation: reveal at once
    // when reduced-motion is requested or IntersectionObserver is unavailable.
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.unobserve(el);
        }
      },
      // threshold 0 + a bottom inset fires reliably as the element scrolls up
      // into view, independent of how tall the element is.
      { threshold: 0, rootMargin: "0px 0px -10% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out ${
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      } ${className}`}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
